import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Trash2, Edit, Plus, LogOut, MapPin, Calendar, ArrowLeft, Link, Loader2, Settings, Mail, MessageSquare, Send, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  
  // Settings state
  const [notificationEmail, setNotificationEmail] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Hudle link states
  const [venueHudleLink, setVenueHudleLink] = useState('');
  const [eventHudleLink, setEventHudleLink] = useState('');
  
  // Form states
  const [venueForm, setVenueForm] = useState({
    name: '', city: '', sports: '', link: '', image: '', gradient: 'from-blue-500 to-cyan-500'
  });
  const [eventForm, setEventForm] = useState({
    name: '', date: '', time: '', venue: '', link: '', image: '', gradient: 'from-purple-500 to-pink-500'
  });
  const [editingVenue, setEditingVenue] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);

  // Queries state
  const [queries, setQueries] = useState([]);
  const [replies, setReplies] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replierName, setReplierName] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const auth = localStorage.getItem('coplay_admin_auth');
    if (auth) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      if (response.data.success) {
        // Store auth in base64 for Basic Auth
        const auth = btoa(`${credentials.username}:${credentials.password}`);
        localStorage.setItem('coplay_admin_auth', auth);
        setIsLoggedIn(true);
        toast.success('Login successful!');
        fetchData();
      }
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('coplay_admin_auth');
    setIsLoggedIn(false);
    setCredentials({ username: '', password: '' });
    toast.success('Logged out successfully');
  };

  const getAuthHeader = () => {
    const auth = localStorage.getItem('coplay_admin_auth');
    return { Authorization: `Basic ${auth}` };
  };

  const fetchData = async () => {
    try {
      const [venuesRes, eventsRes, queriesRes] = await Promise.all([
        axios.get(`${API}/venues`),
        axios.get(`${API}/events`),
        axios.get(`${API}/contact`)
      ]);
      setVenues(venuesRes.data);
      setEvents(eventsRes.data);
      setQueries(queriesRes.data);

      // Fetch settings and replies
      try {
        const [settingsRes, repliesRes] = await Promise.all([
          axios.get(`${API}/admin/settings`, { headers: getAuthHeader() }),
          axios.get(`${API}/admin/replies`, { headers: getAuthHeader() })
        ]);
        setNotificationEmail(settingsRes.data.notification_email || '');
        setReplies(repliesRes.data);
      } catch (err) {
        console.log('Settings/replies not loaded');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, 
        { notification_email: notificationEmail },
        { headers: getAuthHeader() }
      );
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Reply to query
  const handleReply = async (query) => {
    if (!replierName.trim()) {
      toast.error('Please enter your name before sending');
      return;
    }
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    setIsSendingReply(true);
    try {
      await axios.post(`${API}/admin/reply`, {
        to_email: query.email,
        subject: replySubject || `Re: Your CoPlay inquiry`,
        message: replyMessage,
        submitter_name: query.name,
        replier_name: replierName
      }, { headers: getAuthHeader() });
      toast.success(`Reply sent to ${query.email}`);
      // Refresh replies
      try {
        const repliesRes = await axios.get(`${API}/admin/replies`, { headers: getAuthHeader() });
        setReplies(repliesRes.data);
      } catch (e) {}
      setReplyingTo(null);
      setReplyMessage('');
      setReplySubject('');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  // Fetch from Hudle link
  const fetchVenueFromHudle = async () => {
    if (!venueHudleLink) {
      toast.error('Please enter a Hudle link');
      return;
    }
    
    setIsFetching(true);
    try {
      const response = await axios.post(`${API}/admin/fetch-hudle`, 
        { url: venueHudleLink, type: 'venue' },
        { headers: getAuthHeader() }
      );
      
      const data = response.data.data;
      setVenueForm({
        ...venueForm,
        name: data.name || '',
        city: data.city || '',
        sports: data.sports || '',
        link: data.link || venueHudleLink,
        image: data.image || ''
      });
      toast.success('Details fetched! Please review and fill in any missing fields.');
      setVenueHudleLink('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch details from link');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchEventFromHudle = async () => {
    if (!eventHudleLink) {
      toast.error('Please enter a Hudle link');
      return;
    }
    
    setIsFetching(true);
    try {
      const response = await axios.post(`${API}/admin/fetch-hudle`, 
        { url: eventHudleLink, type: 'event' },
        { headers: getAuthHeader() }
      );
      
      const data = response.data.data;
      setEventForm({
        ...eventForm,
        name: data.name || '',
        date: data.date || '',
        time: data.time || '',
        venue: data.venue || '',
        link: data.link || eventHudleLink,
        image: data.image || ''
      });
      toast.success('Details fetched! Please review and fill in any missing fields.');
      setEventHudleLink('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch details from link');
    } finally {
      setIsFetching(false);
    }
  };

  // Venue CRUD
  const handleVenueSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (editingVenue) {
        await axios.put(`${API}/admin/venues/${editingVenue.id}`, venueForm, { headers: getAuthHeader() });
        toast.success('Venue updated successfully!');
      } else {
        await axios.post(`${API}/admin/venues`, venueForm, { headers: getAuthHeader() });
        toast.success('Venue added successfully!');
      }
      resetVenueForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving venue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditVenue = (venue) => {
    setEditingVenue(venue);
    setVenueForm({
      name: venue.name,
      city: venue.city,
      sports: venue.sports,
      link: venue.link,
      image: venue.image,
      gradient: venue.gradient
    });
  };

  const handleDeleteVenue = async (venueId) => {
    if (!window.confirm('Are you sure you want to delete this venue?')) return;
    
    try {
      await axios.delete(`${API}/admin/venues/${venueId}`, { headers: getAuthHeader() });
      toast.success('Venue deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting venue');
    }
  };

  const resetVenueForm = () => {
    setVenueForm({ name: '', city: '', sports: '', link: '', image: '', gradient: 'from-blue-500 to-cyan-500' });
    setEditingVenue(null);
  };

  // Event CRUD
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, eventForm, { headers: getAuthHeader() });
        toast.success('Event updated successfully!');
      } else {
        await axios.post(`${API}/admin/events`, eventForm, { headers: getAuthHeader() });
        toast.success('Event added successfully!');
      }
      resetEventForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving event');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      date: event.date,
      time: event.time,
      venue: event.venue,
      link: event.link,
      image: event.image,
      gradient: event.gradient
    });
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, { headers: getAuthHeader() });
      toast.success('Event deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting event');
    }
  };

  const resetEventForm = () => {
    setEventForm({ name: '', date: '', time: '', venue: '', link: '', image: '', gradient: 'from-purple-500 to-pink-500' });
    setEditingEvent(null);
  };

  const gradientOptions = [
    'from-blue-500 to-cyan-500',
    'from-purple-500 to-pink-500',
    'from-orange-500 to-red-500',
    'from-emerald-500 to-teal-500',
    'from-indigo-500 to-purple-500',
    'from-pink-500 to-rose-500'
  ];

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-black">
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">CoPlay</span> Admin
            </CardTitle>
            <CardDescription>Sign in to manage venues and events</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <a href="/" className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to Website
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-black">
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">CoPlay</span> Admin
            </h1>
            <a href="/" className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> View Website
            </a>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="venues" className="space-y-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="venues" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Venues ({venues.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Events ({events.length})
            </TabsTrigger>
            <TabsTrigger value="queries" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Queries ({queries.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Venues Tab */}
          <TabsContent value="venues" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{editingVenue ? 'Edit Venue' : 'Add New Venue'}</CardTitle>
                <CardDescription>
                  Paste a Hudle venue link to auto-fill details, or enter manually
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Hudle Link Fetch */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-blue-700 font-semibold flex items-center gap-2 mb-2">
                    <Link className="w-4 h-4" /> Quick Add from Hudle Link
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={venueHudleLink}
                      onChange={(e) => setVenueHudleLink(e.target.value)}
                      placeholder="https://hudle.in/venues/..."
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      onClick={fetchVenueFromHudle}
                      disabled={isFetching}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch Details'}
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Paste link and click "Fetch Details" to auto-fill the form below</p>
                </div>

                <form onSubmit={handleVenueSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="venue-name">Venue Name *</Label>
                      <Input
                        id="venue-name"
                        value={venueForm.name}
                        onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })}
                        placeholder="CoPlay | Location Name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="venue-city">City *</Label>
                      <Input
                        id="venue-city"
                        value={venueForm.city}
                        onChange={(e) => setVenueForm({ ...venueForm, city: e.target.value })}
                        placeholder="Delhi"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="venue-sports">Sports (comma-separated) *</Label>
                    <Input
                      id="venue-sports"
                      value={venueForm.sports}
                      onChange={(e) => setVenueForm({ ...venueForm, sports: e.target.value })}
                      placeholder="Pickleball, Padel, Badminton"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="venue-link">Hudle Venue Link *</Label>
                    <Input
                      id="venue-link"
                      type="url"
                      value={venueForm.link}
                      onChange={(e) => setVenueForm({ ...venueForm, link: e.target.value })}
                      placeholder="https://hudle.in/venues/..."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="venue-image">Image URL *</Label>
                    <Input
                      id="venue-image"
                      type="url"
                      value={venueForm.image}
                      onChange={(e) => setVenueForm({ ...venueForm, image: e.target.value })}
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <div>
                    <Label>Card Color Gradient</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {gradientOptions.map((gradient) => (
                        <button
                          key={gradient}
                          type="button"
                          onClick={() => setVenueForm({ ...venueForm, gradient })}
                          className={`w-12 h-8 rounded-lg bg-gradient-to-r ${gradient} ${venueForm.gradient === gradient ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? 'Saving...' : (editingVenue ? 'Update Venue' : 'Add Venue')}
                    </Button>
                    {editingVenue && (
                      <Button type="button" variant="outline" onClick={resetVenueForm}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Venues List */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {venues.map((venue) => (
                <Card key={venue.id} className="overflow-hidden">
                  <div className="h-32 overflow-hidden">
                    <img src={venue.image} alt={venue.name} className="w-full h-full object-cover" />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg truncate">{venue.name}</h3>
                    <p className="text-sm text-slate-500">{venue.city}</p>
                    <p className="text-xs text-slate-400 mt-1">{venue.sports}</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => handleEditVenue(venue)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteVenue(venue.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <a href={venue.link} target="_blank" rel="noopener noreferrer" className="ml-auto">
                        <Button size="sm" variant="outline">View</Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {venues.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No venues added yet. Add your first venue above!
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</CardTitle>
                <CardDescription>
                  Paste a Hudle event link to auto-fill details, or enter manually
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Hudle Link Fetch */}
                <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-purple-700 font-semibold flex items-center gap-2 mb-2">
                    <Link className="w-4 h-4" /> Quick Add from Hudle Link
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={eventHudleLink}
                      onChange={(e) => setEventHudleLink(e.target.value)}
                      placeholder="https://hudle.in/events/..."
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      onClick={fetchEventFromHudle}
                      disabled={isFetching}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch Details'}
                    </Button>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">Paste link and click "Fetch Details" to auto-fill the form below</p>
                </div>

                <form onSubmit={handleEventSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event-name">Event Name *</Label>
                      <Input
                        id="event-name"
                        value={eventForm.name}
                        onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                        placeholder="Community Pickleball Tournament"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="event-venue">Venue/Location *</Label>
                      <Input
                        id="event-venue"
                        value={eventForm.venue}
                        onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                        placeholder="CoPlay Lajpat Nagar, Delhi"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event-date">Date *</Label>
                      <Input
                        id="event-date"
                        value={eventForm.date}
                        onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                        placeholder="Jan 15, 2025"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="event-time">Time *</Label>
                      <Input
                        id="event-time"
                        value={eventForm.time}
                        onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                        placeholder="6:00 PM - 9:00 PM"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="event-link">Hudle Event Link *</Label>
                    <Input
                      id="event-link"
                      type="url"
                      value={eventForm.link}
                      onChange={(e) => setEventForm({ ...eventForm, link: e.target.value })}
                      placeholder="https://hudle.in/events/..."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="event-image">Image URL *</Label>
                    <Input
                      id="event-image"
                      type="url"
                      value={eventForm.image}
                      onChange={(e) => setEventForm({ ...eventForm, image: e.target.value })}
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <div>
                    <Label>Card Color Gradient</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {gradientOptions.map((gradient) => (
                        <button
                          key={gradient}
                          type="button"
                          onClick={() => setEventForm({ ...eventForm, gradient })}
                          className={`w-12 h-8 rounded-lg bg-gradient-to-r ${gradient} ${eventForm.gradient === gradient ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Add Event')}
                    </Button>
                    {editingEvent && (
                      <Button type="button" variant="outline" onClick={resetEventForm}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Events List */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((event) => (
                <Card key={event.id} className="overflow-hidden">
                  <div className="h-32 overflow-hidden">
                    <img src={event.image} alt={event.name} className="w-full h-full object-cover" />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg truncate">{event.name}</h3>
                    <p className="text-sm text-slate-500">{event.date} • {event.time}</p>
                    <p className="text-xs text-slate-400 mt-1">{event.venue}</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => handleEditEvent(event)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteEvent(event.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <a href={event.link} target="_blank" rel="noopener noreferrer" className="ml-auto">
                        <Button size="sm" variant="outline">View</Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {events.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No events added yet. Add your first event above!
              </div>
            )}
          </TabsContent>

          {/* Queries Tab */}
          <TabsContent value="queries" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contact Form Submissions</CardTitle>
                <CardDescription>
                  View and reply to queries from your website visitors
                </CardDescription>
              </CardHeader>
              <CardContent>
                {queries.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No submissions yet</p>
                ) : (
                  <div className="space-y-4">
                    {[...queries].reverse().map((query, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <p className="font-semibold text-lg">{query.name}</p>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {query.email}</span>
                              <span>{query.phone}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
                              {query.location || 'General'}
                            </span>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(query.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>

                        <p className="text-slate-700 bg-slate-50 p-3 rounded">{query.message}</p>

                        {/* Previous replies */}
                        {replies.filter(r => r.to_email === query.email && r.submitter_name === query.name).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Replies Sent</p>
                            {replies.filter(r => r.to_email === query.email && r.submitter_name === query.name).map((r, rIdx) => (
                              <div key={rIdx} className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="font-medium text-green-800">Replied by: {r.replier_name || 'Unknown'}</span>
                                  <span className="text-xs text-green-600">
                                    {new Date(r.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <p className="text-xs text-green-700 mb-1">Subject: {r.subject}</p>
                                <p className="text-green-900 whitespace-pre-wrap">{r.message}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {replyingTo === index ? (
                          <div className="border-t pt-3 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-sm font-medium mb-1 block">Your Name <span className="text-red-500">*</span></Label>
                                <Input
                                  value={replierName}
                                  onChange={(e) => setReplierName(e.target.value)}
                                  placeholder="Enter your name"
                                />
                              </div>
                              <div>
                                <Label className="text-sm font-medium mb-1 block">Subject</Label>
                                <Input
                                  value={replySubject}
                                  onChange={(e) => setReplySubject(e.target.value)}
                                  placeholder={`Re: Your CoPlay inquiry`}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm font-medium mb-1 block">Reply Message <span className="text-red-500">*</span></Label>
                              <textarea
                                className="w-full min-h-[100px] p-3 border rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder="Type your reply..."
                              />
                            </div>
                            <p className="text-xs text-slate-400">
                              Replying as: <strong>{replierName || '(enter your name)'}</strong> | Sending from: noreply@coplay.in → {query.email}
                            </p>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleReply(query)}
                                disabled={isSendingReply || !replierName.trim()}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {isSendingReply ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                Send Reply
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => { setReplyingTo(null); setReplyMessage(''); setReplySubject(''); }}
                              >
                                <X className="w-4 h-4 mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReplyingTo(index)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Mail className="w-4 h-4 mr-1" /> Reply
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>
                  Configure where form submissions are sent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-blue-700 font-semibold flex items-center gap-2 mb-3">
                    <Mail className="w-4 h-4" /> Form Notification Emails
                  </Label>
                  <p className="text-sm text-blue-600 mb-4">
                    Contact form submissions will be sent to these email addresses. Add multiple emails separated by commas.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={notificationEmail}
                      onChange={(e) => setNotificationEmail(e.target.value)}
                      placeholder="email1@example.com, email2@example.com"
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSaveSettings}
                      disabled={isSavingSettings}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isSavingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-2">How it works</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• When someone fills out the contact form on your website, a notification will be sent to all emails listed above</li>
                    <li>• Separate multiple email addresses with commas (e.g., admin@company.com, sales@company.com)</li>
                    <li>• All form submissions are also stored in the database</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
